import React, { Fragment, ReactNode, isValidElement } from 'react';
import { LucideIcon } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { cn } from '@/lib/utils';

/**
 * Single sanctioned accent treatment for hero titles.
 * Wrap the emphasized part of a hero title in <HeroAccent>, or pass a plain
 * title to PageHero and it will apply this automatically.
 */
export const HeroAccent = ({ children, className }: { children: ReactNode; className?: string }) => (
  <span className={cn('bg-gradient-to-r from-amber-300 to-yellow-400 bg-clip-text text-transparent drop-shadow-sm', className)}>
    {children}
  </span>
);
HeroAccent.displayName = 'HeroAccent';

/** Standard stat tile for hero stat rows (e.g. In-Demand Careers). */
export const HeroStatTile = ({
  icon: Icon,
  value,
  label,
}: {
  icon?: LucideIcon;
  value: ReactNode;
  label: ReactNode;
}) => (
  <div className="bg-white/10 backdrop-blur-sm rounded-lg p-4">
    {Icon && <Icon className="h-6 w-6 mx-auto mb-1" />}
    <div className="text-2xl font-bold">{value}</div>
    <div className="text-xs text-white/70">{label}</div>
  </div>
);

function isHeroAccentElement(node: ReactNode): boolean {
  if (!isValidElement(node)) return false;
  const type = node.type;
  if (type === HeroAccent) return true;
  return typeof type === 'function' && (type as { displayName?: string }).displayName === 'HeroAccent';
}

function containsHeroAccent(node: ReactNode): boolean {
  if (isHeroAccentElement(node)) return true;
  if (Array.isArray(node)) return node.some(containsHeroAccent);
  if (isValidElement(node)) {
    return React.Children.toArray(
      (node.props as { children?: ReactNode }).children
    ).some(containsHeroAccent);
  }
  return false;
}

/**
 * Homepage-style yellow accent on the trailing phrase of a hero title.
 * - "... in California" → accent the location after the last " in "
 * - ≤3 words → accent the last word
 * - else → accent the last two words
 */
function splitAccent(text: string): ReactNode {
  const trimmed = text.trim();
  if (!trimmed) return text;

  const leading = text.match(/^\s*/)?.[0] ?? '';
  const trailing = text.match(/\s*$/)?.[0] ?? '';

  const inMatch = trimmed.match(/^(.*\bin\s+)(.+)$/i);
  if (inMatch) {
    const [, prefix, location] = inMatch;
    return (
      <>
        {leading}
        {prefix}
        <HeroAccent>{location}</HeroAccent>
        {trailing}
      </>
    );
  }

  const words = trimmed.split(/\s+/).filter(Boolean);
  const isWordy = (w: string) => /[A-Za-z0-9]/.test(w);

  if (words.length <= 1) {
    return (
      <>
        {leading}
        <HeroAccent>{trimmed}</HeroAccent>
        {trailing}
      </>
    );
  }

  // Skip trailing punctuation-only tokens (e.g. "&" in "In-Demand Careers &")
  let end = words.length;
  while (end > 0 && !isWordy(words[end - 1])) end--;
  if (end === 0) return text;

  const wordyCount = words.slice(0, end).filter(isWordy).length;
  const accentCount = wordyCount <= 3 ? 1 : 2;

  let start = end;
  let collected = 0;
  while (start > 0 && collected < accentCount) {
    start--;
    if (isWordy(words[start])) collected++;
  }

  const head = words.slice(0, start).join(' ');
  const accent = words.slice(start, end).join(' ');
  const suffix = words.slice(end).join(' ');
  return (
    <>
      {leading}
      {head ? `${head} ` : ''}
      <HeroAccent>{accent}</HeroAccent>
      {suffix ? ` ${suffix}` : ''}
      {trailing}
    </>
  );
}

function flattenChildren(node: ReactNode): ReactNode[] {
  if (node == null || typeof node === 'boolean') return [];
  if (Array.isArray(node)) return node.flatMap(flattenChildren);
  if (isValidElement(node) && (node.type === Fragment || node.type === React.Fragment)) {
    return React.Children.toArray(
      (node.props as { children?: ReactNode }).children
    ).flatMap(flattenChildren);
  }
  return [node];
}

/**
 * Apply brand yellow accent to PageHero titles that do not already use HeroAccent.
 */
export function applyHeroTitleAccent(title: ReactNode): ReactNode {
  if (title == null || title === false) return title;
  if (containsHeroAccent(title)) return title;

  if (typeof title === 'string' || typeof title === 'number') {
    return splitAccent(String(title));
  }

  const children = flattenChildren(title);
  if (children.length === 0) return title;
  if (children.some(isHeroAccentElement)) return title;

  const isPrimitive = (c: ReactNode) =>
    c == null || typeof c === 'string' || typeof c === 'number' || typeof c === 'boolean';

  if (children.every(isPrimitive)) {
    return splitAccent(children.filter((c) => c != null && c !== false).join(''));
  }

  // Mixed (e.g. title text + subtitle span): accent only the first string child
  const firstStringIdx = children.findIndex(
    (c) => typeof c === 'string' || typeof c === 'number'
  );
  if (firstStringIdx === -1) return title;

  const next = [...children];
  next[firstStringIdx] = splitAccent(String(next[firstStringIdx]));
  return <>{next}</>;
}

interface PageHeroProps {
  /** Optional pill above the title. One standard style across the site. */
  badge?: { icon?: LucideIcon; label: ReactNode };
  title: ReactNode;
  /** 'default' = text-4xl md:text-5xl, 'large' = text-4xl md:text-6xl (homepage-scale) */
  titleSize?: 'default' | 'large';
  subtitle?: ReactNode;
  /** Tailwind max-width class for the inner container. */
  maxWidth?: 'max-w-2xl' | 'max-w-3xl' | 'max-w-4xl';
  className?: string;
  /** CTAs, stat tiles, embedded tools — rendered below the subtitle. */
  children?: ReactNode;
}

/**
 * Unified page hero: one brand gradient everywhere.
 * Topics are differentiated via badge icon/label and <HeroAccent> title spans,
 * not background color. Plain string/text titles get trailing HeroAccent automatically.
 */
const PageHero = ({
  badge,
  title,
  titleSize = 'default',
  subtitle,
  maxWidth = 'max-w-4xl',
  className,
  children,
}: PageHeroProps) => {
  const BadgeIcon = badge?.icon;
  const accentedTitle = applyHeroTitleAccent(title);

  return (
    <section
      className={cn(
        'relative py-16 px-4 text-center bg-gradient-to-br from-wage-primary to-wage-primary-light text-white',
        className
      )}
    >
      <div className={cn('container mx-auto', maxWidth)}>
        {badge && (
          <Badge variant="outline" className="mb-6 border-white/20 bg-white/10 text-white">
            {BadgeIcon && <BadgeIcon className="w-4 h-4 mr-2" />}
            {badge.label}
          </Badge>
        )}
        <h1
          className={cn(
            'font-bold mb-5 animate-fade-in',
            titleSize === 'large' ? 'text-4xl md:text-6xl' : 'text-4xl md:text-5xl'
          )}
        >
          {accentedTitle}
        </h1>
        {subtitle && (
          <p
            className="text-lg md:text-xl mb-8 text-white/80 max-w-2xl mx-auto animate-fade-in"
            style={{ animationDelay: '0.1s' }}
          >
            {subtitle}
          </p>
        )}
        {children}
      </div>
    </section>
  );
};

export default PageHero;
